import { useState, useEffect, useCallback, Fragment } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { FiLoader, FiChevronLeft, FiChevronRight, FiChevronDown, FiClock } from 'react-icons/fi'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBarraSep, PlnBloco, stickyTh } from './PanelShell'

// Histórico (PRD §4): trilha de auditoria (audit_log), somente leitura.

interface Entry {
  id: number; entity_type: string; entity_id: string | null; action: string
  user_id: string | null; user_name: string | null
  before_json: unknown; after_json: unknown; created_at: string
}
interface Data { items: Entry[]; total: number; page: number; pageSize: number }

const ENTITY_LABELS: Record<string, string> = {
  transaction: 'Lançamento', sale: 'Venda', transfer: 'Transferência',
  bank_account: 'Conta', financial_category: 'Categoria', cost_center: 'Centro de custo',
  company: 'Empresa', charge_config: 'Cobrança', pix_limit: 'Limite Pix',
}
const ACTION_META: Record<string, { label: string; cls: string }> = {
  create: { label: 'Criou',      cls: 'neu-tag bg-primary/10 text-primary' },
  update: { label: 'Editou',     cls: 'neu-tag bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  delete: { label: 'Excluiu',    cls: 'neu-tag bg-red-500/10 text-red-600 dark:text-red-400' },
  pay:    { label: 'Baixou',     cls: 'neu-tag bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  import: { label: 'Importou',   cls: 'neu-tag bg-violet-500/10 text-violet-600 dark:text-violet-400' },
}
const ENTITY_OPTS: [string, string][] = [['', 'Todas as entidades'], ...Object.entries(ENTITY_LABELS)]
const ACTION_OPTS: [string, string][] = [['', 'Todas as ações'], ...Object.entries(ACTION_META).map(([k, v]) => [k, v.label] as [string, string])]

const ptDateTime = (s: string) => {
  const [d, t] = s.split(' ')
  return `${d.split('-').reverse().join('/')} ${t?.slice(0, 5) || ''}`
}
const asObj = (v: unknown): Record<string, unknown> | null => {
  if (v == null) return null
  if (typeof v === 'string') { try { return JSON.parse(v) } catch { return null } }
  return typeof v === 'object' ? v as Record<string, unknown> : null
}

export function Historico() {
  const { authenticatedFetch } = useAuth()
  const [data, setData] = useState<Data | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [entity, setEntity] = useState('')
  const [action, setAction] = useState('')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '25' })
      if (entity) params.set('entity', entity)
      if (action) params.set('action', action)
      const r = await authenticatedFetch(`/api/historico?${params}`)
      const d = await r.json()
      if (d.success) setData(d); else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, entity, action, page])
  useEffect(() => { load() }, [load])

  const items = data?.items || []
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  const resetPageAnd = (fn: () => void) => { fn(); setPage(1) }

  return (
    <PanelShell
      titulo="Histórico"
      sub="Quem criou, editou, baixou ou excluiu o quê, e quando"
      icone={<FiClock />}>
      {/* PLANOS (21/10/2026): filtros numa PlnBarra; a contagem de registros
          foi pra nota do bloco e a paginação pro canto direito dele. */}
      <PlnBarra>
        <PlnBarraRot>Entidade</PlnBarraRot>
        <select value={entity} onChange={e => resetPageAnd(() => setEntity(e.target.value))} className="neu-convex rounded-lg border border-border bg-card px-3 py-2 text-sm">
          {ENTITY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <PlnBarraSep />
        <PlnBarraRot>Ação</PlnBarraRot>
        <select value={action} onChange={e => resetPageAnd(() => setAction(e.target.value))} className="neu-convex rounded-lg border border-border bg-card px-3 py-2 text-sm">
          {ACTION_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </PlnBarra>

      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {/* Tabela — única região com scroll; cabeçalho sticky (padrão PanelShell) */}
      <PlnBloco
        titulo="Trilha de auditoria"
        nota={data ? `${data.total} registro${data.total === 1 ? '' : 's'} · somente leitura` : undefined}
        extra={data && totalPages > 1 ? (
          /* Paginação — no cabeçalho do bloco, sempre visível */
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} title="Página anterior" className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary disabled:opacity-40"><FiChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-muted-foreground">Página {page} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} title="Próxima página" className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary disabled:opacity-40"><FiChevronRight className="w-4 h-4" /></button>
          </div>
        ) : undefined}
        className="flex-1 min-h-[280px]"
      >
      <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium w-40 ${stickyTh}`}>Data/hora</th>
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Usuário</th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Ação</th>
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Registro</th>
              <th className={`px-4 py-2.5 font-medium w-10 ${stickyTh}`}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-14 text-center text-muted-foreground">
                <FiClock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Nenhuma movimentação registrada ainda.
              </td></tr>
            ) : items.map(it => {
              const am = ACTION_META[it.action] || { label: it.action, cls: 'bg-secondary text-muted-foreground' }
              const before = asObj(it.before_json), after = asObj(it.after_json)
              const detail = after || before
              const isOpen = expanded === it.id
              return (
                <Fragment key={it.id}>
                  <tr className="neu-tr border-b border-border/60 hover:bg-secondary/40">
                    <td className="px-4 py-2.5 text-foreground whitespace-nowrap">{ptDateTime(it.created_at)}</td>
                    <td className="px-4 py-2.5 text-foreground truncate">{it.user_name || it.user_id || '—'}</td>
                    <td className="px-4 py-2.5"><span className={`text-xs font-medium rounded-full px-2 py-0.5 ${am.cls}`}>{am.label}</span></td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {ENTITY_LABELS[it.entity_type] || it.entity_type}{it.entity_id && it.entity_id !== '0' ? <span className="text-foreground"> #{it.entity_id}</span> : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {detail && (
                        <button onClick={() => setExpanded(isOpen ? null : it.id)} title="Detalhes" className="neu-tab p-1 rounded hover:bg-secondary text-muted-foreground">
                          <FiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && detail && (
                    <tr className="neu-band-1 bg-secondary/20 border-b border-border/60">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-xs">
                          {Object.entries(detail).map(([k, v]) => (
                            <div key={k} className="flex gap-2 min-w-0">
                              <span className="text-muted-foreground shrink-0">{k}:</span>
                              <span className="text-foreground truncate">{v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      </PlnBloco>
    </PanelShell>
  )
}
